import type { ForecastProbs, ForecastState, IntelData } from "./translate";

/**
 * fbeForecastView.ts — single dashboard source of truth for Forecast Belief
 * Engine presentation.
 *
 * The Forecast Belief Engine is LOG-ONLY/watch-only. These helpers only turn
 * persisted forecast_* fields into honest UI labels; they never influence
 * execution, alerts, sizing, signal gates, or top-card HMM/Kronos verdicts.
 */

export const FBE_DIRECTIONAL_DISPLAY_MIN_CONFIDENCE = 50;

const PROMISING_SAMPLE_SIZE = 50;
const VALIDATED_SAMPLE_SIZE = 100;
const ACCEPTABLE_BRIER = 0.42;
const GOOD_BRIER = 0.32;
const ACCEPTABLE_WIN_RATE = 0.55;
const GOOD_WIN_RATE = 0.58;
export const FBE_SESSION_FORECAST_CONTRACT = "session_forecast";
export const FBE_SESSION_OPEN_ANCHOR_TYPE = "session_open";

type FbeState = ForecastState | string;
type FbeLane = "production" | "candidate";
type FbeBias = "bullish" | "bearish" | "ranging" | "no_edge" | "unavailable";
type FbeConfidenceTier = "high" | "medium" | "low" | "unavailable";
export type FbeReliabilityTier = "validated" | "promising" | "watch_only" | "low_sample" | "stale" | "unavailable";

export interface FbeForecastFields {
  session?: string;
  pending_session?: string;
  session_label?: string;
  date?: string;
  forecast_raw_state?: FbeState;
  forecast_raw_confidence?: number;
  forecast_raw_probs?: ForecastProbs | unknown;
  forecast_stable_state?: FbeState;
  forecast_stable_confidence?: number;
  forecast_transition_probability?: number;
  forecast_state_age_min?: number;
  forecast_flip_suppressed?: boolean;
  forecast_stability_score?: number;
  forecast_reason?: string;
  forecast_engine_version?: string;
  forecast_generated_at?: number;
  forecast_generated_at_iso?: string;
  forecast_contract?: string;
  forecast_session_id?: string;
  forecast_anchor_type?: string;
  forecast_anchor_price?: number;
  forecast_anchor_preserved?: boolean;
  forecast_authoritative_channel?: string;
  forecast_candidate_channel?: string;
  forecast_release_policy?: string;
  forecast_accuracy_status?: string;
  forecast_data_integrity_status?: string;
  forecast_data_integrity_issues?: string;
  forecast_trust_status?: string;
  forecast_trading_approved?: boolean;
  forecast_calibration_sample_size?: number;
  forecast_calibration_wins?: number;
  forecast_calibration_losses?: number;
  forecast_calibration_flats?: number;
  forecast_calibration_win_rate?: number;
  forecast_calibration_avg_brier?: number;
  forecast_trader_bias?: FbeBias | string;
  forecast_trade_mode?: string;
  forecast_preferred_setups?: string[] | unknown;
  forecast_avoid_setups?: string[] | unknown;
  forecast_guidance_reason?: string;
  forecast_guidance_scope?: string;
  forecast_guidance_contract?: string;
  forecast_guidance_execution_approved?: boolean;
  candidate_forecast_raw_state?: FbeState;
  candidate_forecast_raw_confidence?: number;
  candidate_forecast_raw_probs?: ForecastProbs | unknown;
  candidate_forecast_stable_state?: FbeState;
  candidate_forecast_stable_confidence?: number;
  candidate_forecast_transition_probability?: number;
  candidate_forecast_state_age_min?: number;
  candidate_forecast_flip_suppressed?: boolean;
  candidate_forecast_stability_score?: number;
  candidate_forecast_reason?: string;
  candidate_forecast_engine_version?: string;
  candidate_forecast_contract?: string;
  candidate_forecast_session_id?: string;
  candidate_forecast_anchor_type?: string;
  candidate_forecast_anchor_price?: number;
  candidate_forecast_anchor_preserved?: boolean;
  candidate_forecast_calibration_sample_size?: number;
  candidate_forecast_calibration_win_rate?: number;
  candidate_forecast_calibration_avg_brier?: number;
  forecast_scope?: string;
  forecast_phase?: string;
  forecast_session_start_ms?: number;
  forecast_session_end_ms?: number;
  candidate_forecast_session_start_ms?: number;
  candidate_forecast_session_end_ms?: number;
}

export interface SelectedFbeForecastView {
  view: FbeForecastView;
  lane: FbeLane;
  engineVersion?: string;
  sampleSize?: number;
  winRate?: number;
  avgBrier?: number;
  accuracyLabel: string;
}

export interface FbeForecastView {
  /** Primary label safe to display as the headline. */
  primaryLabel: string;
  /** Full stable-state detail. */
  stableDetail: string;
  rawLine: string;
  sessionLabel: string;
  flipSuppressed: boolean;
  stateColor: string;
  displayState: string;
  stableState: string;
  rawState: string;
  lowConfidenceDirectional: boolean;
  reason?: string;
}

export function isAnchoredFbeSessionForecast(
  data: Pick<FbeForecastFields, "forecast_contract" | "forecast_anchor_type">,
): boolean {
  return (
    data.forecast_contract === FBE_SESSION_FORECAST_CONTRACT &&
    data.forecast_anchor_type === FBE_SESSION_OPEN_ANCHOR_TYPE
  );
}

export function expectedFbeSessionId(
  data: Pick<FbeForecastFields, "session" | "pending_session" | "date">,
): string | null {
  const session = (data.session ?? data.pending_session ?? "").toLowerCase();
  if (!session || !data.date) return null;
  return `${session}:${data.date}`;
}

export function hasExpectedFbeSessionId(
  data: Pick<FbeForecastFields, "session" | "pending_session" | "date" | "forecast_session_id">,
): boolean {
  const expected = expectedFbeSessionId(data);
  return expected !== null && data.forecast_session_id === expected;
}

export function isScoreableFbeSessionForecast(
  data: Pick<
    FbeForecastFields,
    "session" | "pending_session" | "date" | "forecast_contract" | "forecast_anchor_type" | "forecast_session_id"
  >,
): boolean {
  return isAnchoredFbeSessionForecast(data) && hasExpectedFbeSessionId(data);
}

export interface FbeCardGuidance {
  bias: FbeBias;
  primaryText: string;
  confidenceText: string;
  reliabilityText: string;
  statusText: string;
  sessionLabel: string;
  laneLabel?: string;
  engineVersion?: string;
  rawText?: string;
  reliabilityTier: FbeReliabilityTier;
  confidenceTier: FbeConfidenceTier;
  confidencePct?: number;
  stateColor: string;
  tradeMode?: string;
  preferredSetups: string[];
  avoidSetups: string[];
  guidanceReason?: string;
  guidanceContract?: string;
  dataLine: string;
  governanceLine: string;
  calibrationLine: string;
  executionApproved: boolean;
  isAvailable: boolean;
}

interface FbeForecastOption {
  lane: FbeLane;
  view: FbeForecastView;
  engineVersion?: string;
  sampleSize?: number;
  winRate?: number;
  avgBrier?: number;
  generatedAt?: number;
  confidencePct?: number;
  dataIntegrityStatus?: string;
  tradingApproved?: boolean;
}

const FORECAST_STATE_COLORS: Record<string, string> = {
  // FBE remains LOG-ONLY/watch-only, but the regime colors should match trader
  // intuition: bullish green, bearish red. Execution safety is carried by the
  // explicit watch-only labels and tests, not by making direction colors muddy.
  bullish: "#22c55e",
  bearish: "#ef4444",
  flat: "#a0a0b8",
  mixed: "#eab308",
  unstable: "#f97316",
  no_clear: "#eab308",
  unavailable: "#71717a",
};

function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function clampPct(value: number): number {
  return Math.max(0, Math.min(100, value));
}

function isDirectionalState(state: string | undefined): boolean {
  return state === "bullish" || state === "bearish";
}

function normalizedStateToBias(state: string | undefined): FbeBias {
  if (state === "bullish" || state === "bearish") return state;
  if (state === "flat") return "ranging";
  if (state === "mixed" || state === "unstable" || state === "no_clear") return "no_edge";
  return "unavailable";
}

function forecastStateDisplayLabel(state: string | undefined): string {
  if (!state) return "—";
  if (state === "flat") return "SIDEWAYS/RANGING";
  return state.toUpperCase();
}

function normalizeTraderBias(value: unknown, fallbackState: string | undefined): FbeBias {
  const bias = typeof value === "string" ? value.toLowerCase() : "";
  if (bias === "bullish" || bias === "bearish" || bias === "ranging" || bias === "no_edge") return bias;
  return normalizedStateToBias(fallbackState);
}

function stringList(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === "string" && item.length > 0) : [];
}

function setupLabel(value: string): string {
  return value.replace(/_/g, " ");
}

function biasText(bias: FbeBias, sessionLabel: string, confidencePct?: number): string {
  const session = sessionLabel.replace(/ Session-End Forecast$/i, "");
  const confidencePrefix = isFiniteNumber(confidencePct) && confidencePct < FBE_DIRECTIONAL_DISPLAY_MIN_CONFIDENCE
    ? "Low-confidence "
    : "";
  if (bias === "bullish") return `${confidencePrefix}Bullish into ${session} close`;
  if (bias === "bearish") return `${confidencePrefix}Bearish into ${session} close`;
  if (bias === "ranging") return `${confidencePrefix}Ranging into ${session} close`;
  if (bias === "no_edge") return "No FBE edge — mechanical signals remain sole source";
  return "No reliable forecast yet";
}

function neutralizeFbeGuidanceReason(reason: string | undefined): string | undefined {
  if (!reason) return undefined;
  return reason
    .replace(/\bprioritize\b/gi, "context:")
    .replace(/\bfavor\b/gi, "context:")
    .replace(/\buse\s+only\b/gi, "context only:")
    .replace(/\bprefer\b/gi, "context:");
}

function rawStateText(rawState: string, rawLine: string): string | undefined {
  if (!rawState) return undefined;
  const label = rawLine.replace(/^Raw:\s*/i, "Raw ");
  if (rawState === "bullish" || rawState === "bearish") return `${label} · not stable yet`;
  if (rawState === "mixed" || rawState === "unstable") return `${label} · no raw edge`;
  if (rawState === "flat" || rawState === "no_clear") return `${label} · no directional raw edge`;
  return label;
}

function reliabilityTier(sampleSize?: number, winRate?: number, avgBrier?: number, stale = false): FbeReliabilityTier {
  if (stale) return "stale";
  if (!isFiniteNumber(sampleSize) || sampleSize <= 0) return "unavailable";
  if (sampleSize < 20) return "low_sample";

  const hasAcceptableWinRate = isFiniteNumber(winRate) && winRate >= ACCEPTABLE_WIN_RATE;
  const hasGoodWinRate = isFiniteNumber(winRate) && winRate >= GOOD_WIN_RATE;
  const hasAcceptableBrier = !isFiniteNumber(avgBrier) || avgBrier <= ACCEPTABLE_BRIER;
  const hasGoodBrier = !isFiniteNumber(avgBrier) || avgBrier <= GOOD_BRIER;

  if (sampleSize >= VALIDATED_SAMPLE_SIZE && hasGoodWinRate && hasGoodBrier) return "validated";
  if (sampleSize >= PROMISING_SAMPLE_SIZE && hasAcceptableWinRate && hasAcceptableBrier) return "promising";
  return "watch_only";
}

function reliabilityLabel(tier: FbeReliabilityTier, sampleSize?: number): string {
  const sampleLabel = isFiniteNumber(sampleSize) && sampleSize > 0 ? ` · ${Math.round(sampleSize)} scored` : "";
  if (tier === "validated") return `Validated${sampleLabel}`;
  if (tier === "promising") return `Promising${sampleLabel}`;
  if (tier === "watch_only") return `Watch-only${sampleLabel}`;
  if (tier === "low_sample") return `Low sample${sampleLabel}`;
  if (tier === "stale") return "Stale forecast";
  return "Unavailable";
}

function confidenceTier(confidencePct?: number, reliability: FbeReliabilityTier = "unavailable"): FbeConfidenceTier {
  if (!isFiniteNumber(confidencePct)) return "unavailable";
  // Do not visually promote unproven forecasts as high-confidence on the main
  // card. Raw calibration detail belongs in Accountability.
  if (reliability === "low_sample" || reliability === "unavailable" || reliability === "stale") {
    return confidencePct >= 60 ? "medium" : "low";
  }
  if (confidencePct >= 70) return "high";
  if (confidencePct >= 55) return "medium";
  return "low";
}

function confidenceLabel(tier: FbeConfidenceTier, confidencePct?: number): string {
  if (!isFiniteNumber(confidencePct) || tier === "unavailable") return "Confidence unavailable";
  const label = tier === "high" ? "High" : tier === "medium" ? "Medium" : "Low";
  return `${label} confidence · ${Math.round(confidencePct)}%`;
}

export function formatFbeConfidence(value: unknown): string {
  if (!isFiniteNumber(value)) return "";
  return `${Math.round(clampPct(value))}%`;
}

export function formatFbeStateAge(minutes: unknown): string {
  if (!isFiniteNumber(minutes)) return "—";
  if (minutes < 1) return "<1m";
  if (minutes < 60) return `${Math.round(minutes)}m`;
  const hours = Math.floor(minutes / 60);
  const rem = Math.round(minutes - hours * 60);
  return rem === 0 ? `${hours}h` : `${hours}h ${rem}m`;
}

export function fbeSessionLabel(data: Pick<IntelData, "session" | "pending_session" | "session_label" | "forecast_phase" | "forecast_scope" | "forecast_contract" | "forecast_anchor_type">): string {
  const session = (data.session ?? data.pending_session ?? "").toLowerCase();
  const base = data.session_label ?? (session === "us" ? "NY/US" : session === "asia" ? "Asia" : "Current");
  const phase = data.forecast_phase ?? data.forecast_scope;

  if (isAnchoredFbeSessionForecast(data)) return `${base} Session-End Forecast`;
  if (phase === "pre_session") return `Rolling ${base} Forecast Prior`;
  if (phase === "post_session") return `${base} Post-Session Forecast Review`;
  return `${base} Diagnostic Forecast`;
}

export function buildFbeForecastView(data: FbeForecastFields): FbeForecastView | undefined {
  const stableState = data.forecast_stable_state?.toString().toLowerCase();
  const rawState = data.forecast_raw_state?.toString().toLowerCase();

  // No forecast_* state at all means this row/snapshot has no FBE payload.
  if (!stableState && !rawState) return undefined;

  const upperStable = forecastStateDisplayLabel(stableState);
  const upperRaw = forecastStateDisplayLabel(rawState);
  const stableConf = formatFbeConfidence(data.forecast_stable_confidence);
  const rawConf = formatFbeConfidence(data.forecast_raw_confidence);

  const stableConfidenceValue = isFiniteNumber(data.forecast_stable_confidence)
    ? clampPct(data.forecast_stable_confidence)
    : null;
  const lowConfidenceDirectional =
    isDirectionalState(stableState) &&
    stableConfidenceValue !== null &&
    stableConfidenceValue < FBE_DIRECTIONAL_DISPLAY_MIN_CONFIDENCE;

  const stableDetail = stableConf ? `${upperStable} ${stableConf}` : upperStable;
  const primaryLabel = stableDetail;
  const displayState = stableState ?? rawState ?? "";

  const rawParts: string[] = [];
  rawParts.push(rawConf ? `Raw: ${upperRaw} ${rawConf}` : `Raw: ${upperRaw}`);
  if (isFiniteNumber(data.forecast_stability_score)) {
    rawParts.push(`Stability ${Math.round(data.forecast_stability_score)}`);
  }
  // Do not include forecast_state_age_min in the compact row. It is stable-
  // state dwell time, not data freshness, and displaying multi-hour dwell in
  // the forecast chip makes fresh realtime data look stale. Actual freshness
  // is shown separately by the card's Convex/WebSocket source label.

  return {
    primaryLabel,
    stableDetail,
    rawLine: rawParts.join(" · "),
    sessionLabel: fbeSessionLabel(data),
    flipSuppressed: data.forecast_flip_suppressed === true,
    stateColor: FORECAST_STATE_COLORS[displayState] ?? "#a0a0b8",
    displayState,
    stableState: stableState ?? rawState ?? "",
    rawState: rawState ?? "",
    lowConfidenceDirectional,
    reason: data.forecast_reason,
  };
}

export function formatFbeRawProbs(data: Pick<IntelData, "forecast_raw_probs">): string | undefined {
  const probs = data.forecast_raw_probs;
  if (!probs || typeof probs !== "object") return undefined;
  const order = ["bullish", "bearish", "flat", "mixed_unstable"] as const;
  const seen: string[] = [];
  for (const key of order) {
    const p = (probs as unknown as Record<string, unknown>)[key];
    if (!isFiniteNumber(p)) continue;
    const pct = Math.round(Math.max(0, Math.min(1, p)) * 100);
    const label = key === "mixed_unstable" ? "mix" : key === "flat" ? "sideways" : key.slice(0, 4);
    seen.push(`${label} ${pct}%`);
  }
  return seen.length ? seen.join(" / ") : undefined;
}

function candidateForecastFields(data: FbeForecastFields): FbeForecastFields {
  return {
    session: data.session,
    pending_session: data.pending_session,
    session_label: data.session_label,
    date: data.date,
    forecast_raw_state: data.candidate_forecast_raw_state,
    forecast_raw_confidence: data.candidate_forecast_raw_confidence,
    forecast_raw_probs: data.candidate_forecast_raw_probs,
    forecast_stable_state: data.candidate_forecast_stable_state,
    forecast_stable_confidence: data.candidate_forecast_stable_confidence,
    forecast_transition_probability: data.candidate_forecast_transition_probability,
    forecast_state_age_min: data.candidate_forecast_state_age_min,
    forecast_flip_suppressed: data.candidate_forecast_flip_suppressed,
    forecast_stability_score: data.candidate_forecast_stability_score,
    forecast_reason: data.candidate_forecast_reason,
    forecast_engine_version: data.candidate_forecast_engine_version,
    forecast_contract: data.candidate_forecast_contract,
    forecast_session_id: data.candidate_forecast_session_id,
    forecast_anchor_type: data.candidate_forecast_anchor_type,
    forecast_anchor_price: data.candidate_forecast_anchor_price,
    forecast_anchor_preserved: data.candidate_forecast_anchor_preserved,
    forecast_session_start_ms: data.candidate_forecast_session_start_ms,
    forecast_session_end_ms: data.candidate_forecast_session_end_ms,
    forecast_calibration_sample_size: data.candidate_forecast_calibration_sample_size,
    forecast_calibration_win_rate: data.candidate_forecast_calibration_win_rate,
    forecast_calibration_avg_brier: data.candidate_forecast_calibration_avg_brier,
  };
}

function isEligibleForBiasWorkspace(): boolean {
  // FBE Bias workspace owns trader guidance display. Data-integrity status is
  // governance/warning metadata, not a selector that should fall back to some
  // unrelated Command Center or Markov view.
  return true;
}

function calibrationScore(sampleSize?: number, winRate?: number, avgBrier?: number): number {
  if (!isFiniteNumber(sampleSize) || sampleSize <= 0) return -1;
  const tier = reliabilityTier(sampleSize, winRate, avgBrier);
  const tierWeight = tier === "validated" ? 4 : tier === "promising" ? 3 : tier === "watch_only" ? 2 : tier === "low_sample" ? 1 : 0;
  const wr = isFiniteNumber(winRate) ? winRate : 0.5;
  const brierComponent = isFiniteNumber(avgBrier) ? 1 - Math.max(0, Math.min(1, avgBrier)) : 0.5;
  const sampleConfidence = Math.min(1, sampleSize / VALIDATED_SAMPLE_SIZE);
  return tierWeight + (brierComponent * 0.45) + (wr * 0.35) + (sampleConfidence * 0.20);
}

function formatAccuracyLabel(sampleSize?: number, winRate?: number, avgBrier?: number): string {
  if (!isFiniteNumber(sampleSize) || sampleSize <= 0) return "no completed calibration yet";
  const parts = [`n=${Math.round(sampleSize)}`];
  if (isFiniteNumber(winRate)) parts.push(`${Math.round(winRate * 100)}% WR`);
  if (isFiniteNumber(avgBrier)) parts.push(`Brier ${avgBrier.toFixed(2)}`);
  return parts.join(" · ");
}

function formatFbeDataLine(data: FbeForecastFields, option?: FbeForecastOption): string {
  if (!option) return "no session-end forecast payload";
  const contract = data.forecast_contract ?? "contract missing";
  const anchor = data.forecast_anchor_type ?? "anchor missing";
  const sessionId = data.forecast_session_id ?? "session id missing";
  const engine = option.engineVersion ? ` · ${option.engineVersion}` : "";
  return `${contract} · ${anchor} · ${sessionId}${engine}`;
}

function formatFbeGovernanceLine(data: FbeForecastFields, option?: FbeForecastOption): string {
  if (!option) return "integrity unavailable · trust unavailable · execution disabled";
  const integrity = data.forecast_data_integrity_status ?? "unknown";
  const trust = data.forecast_trust_status ?? data.forecast_accuracy_status ?? "unknown";
  const approval = option.tradingApproved === true ? "payload approval ignored" : "execution disabled";
  return `integrity ${integrity} · trust ${trust} · ${approval}`;
}

function forecastOptions(data: FbeForecastFields): FbeForecastOption[] {
  const productionView = buildFbeForecastView(data);
  const candidateData = candidateForecastFields(data);
  const candidateView = buildFbeForecastView(candidateData);
  const options: FbeForecastOption[] = [];

  if (productionView && isScoreableFbeSessionForecast(data)) {
    options.push({
      view: productionView,
      lane: "production",
      engineVersion: data.forecast_engine_version,
      sampleSize: data.forecast_calibration_sample_size,
      winRate: data.forecast_calibration_win_rate,
      avgBrier: data.forecast_calibration_avg_brier,
      generatedAt: data.forecast_generated_at,
      confidencePct: data.forecast_stable_confidence,
      dataIntegrityStatus: data.forecast_data_integrity_status,
      tradingApproved: data.forecast_trading_approved,
    });
  }

  if (candidateView && isScoreableFbeSessionForecast(candidateData)) {
    options.push({
      view: candidateView,
      lane: "candidate",
      engineVersion: data.candidate_forecast_engine_version,
      sampleSize: data.candidate_forecast_calibration_sample_size,
      winRate: data.candidate_forecast_calibration_win_rate,
      avgBrier: data.candidate_forecast_calibration_avg_brier,
      generatedAt: data.forecast_generated_at,
      confidencePct: data.candidate_forecast_stable_confidence,
      dataIntegrityStatus: data.forecast_data_integrity_status,
      tradingApproved: false,
    });
  }

  return options.filter(isEligibleForBiasWorkspace);
}

function selectedOption(data: FbeForecastFields): FbeForecastOption | undefined {
  const options = forecastOptions(data);
  if (!options.length) return undefined;
  return options.sort((a, b) => {
    const scoreDelta = calibrationScore(b.sampleSize, b.winRate, b.avgBrier) - calibrationScore(a.sampleSize, a.winRate, a.avgBrier);
    if (scoreDelta !== 0) return scoreDelta;
    return (b.sampleSize ?? 0) - (a.sampleSize ?? 0);
  })[0];
}

function productionOption(data: FbeForecastFields): FbeForecastOption | undefined {
  return forecastOptions(data).find((option) => option.lane === "production");
}

/**
 * Select the most accurate session-forecast FBE lane available on a live card.
 * This is display-only/accountability context: it does not affect getVerdict(),
 * trade labels, alerts, gates, sizing, or execution.
 */
export function selectBestFbeSessionForecast(data: FbeForecastFields): SelectedFbeForecastView | undefined {
  const option = selectedOption(data);
  if (!option) return undefined;
  return {
    view: option.view,
    lane: option.lane,
    engineVersion: option.engineVersion,
    sampleSize: option.sampleSize,
    winRate: option.winRate,
    avgBrier: option.avgBrier,
    accuracyLabel: formatAccuracyLabel(option.sampleSize, option.winRate, option.avgBrier),
  };
}

/**
 * FBE Bias workspace guidance. Use the production forecast lane because the
 * trader-bias fields are produced from forecast_* fields, not from candidate_*
 * experiments. Candidate comparisons stay in accountability/technical detail.
 */
export function buildFbeCardGuidance(data: FbeForecastFields): FbeCardGuidance {
  const option = productionOption(data);
  const fallbackSession = fbeSessionLabel(data);

  if (!option) {
    return {
      bias: "unavailable",
      primaryText: "No reliable forecast yet",
      confidenceText: "Confidence unavailable",
      reliabilityText: "Unavailable",
      statusText: "Session-end guidance unavailable",
      sessionLabel: fallbackSession,
      reliabilityTier: "unavailable",
      confidenceTier: "unavailable",
      stateColor: FORECAST_STATE_COLORS.unavailable ?? "#71717a",
      preferredSetups: [],
      avoidSetups: [],
      dataLine: formatFbeDataLine(data),
      governanceLine: formatFbeGovernanceLine(data),
      calibrationLine: "no completed calibration yet",
      executionApproved: false,
      isAvailable: false,
    };
  }

  const bias = normalizeTraderBias(data.forecast_trader_bias, option.view.stableState);
  const confidencePct = isFiniteNumber(option.confidencePct)
    ? clampPct(option.confidencePct)
    : undefined;
  const reliability = reliabilityTier(option.sampleSize, option.winRate, option.avgBrier);
  const tier = confidenceTier(confidencePct, reliability);
  const laneLabel = option.lane === "candidate" ? "Candidate · watch-only" : reliabilityLabel(reliability, option.sampleSize);

  return {
    bias,
    primaryText: biasText(bias, option.view.sessionLabel, confidencePct),
    confidenceText: confidenceLabel(tier, confidencePct),
    reliabilityText: reliabilityLabel(reliability, option.sampleSize),
    statusText: "Forecast guidance only — not a trade signal",
    sessionLabel: option.view.sessionLabel,
    laneLabel,
    engineVersion: option.engineVersion,
    rawText: rawStateText(option.view.rawState, option.view.rawLine),
    reliabilityTier: reliability,
    confidenceTier: tier,
    confidencePct,
    stateColor: option.view.stateColor,
    tradeMode: data.forecast_trade_mode,
    preferredSetups: stringList(data.forecast_preferred_setups),
    avoidSetups: stringList(data.forecast_avoid_setups),
    guidanceReason: neutralizeFbeGuidanceReason(data.forecast_guidance_reason),
    guidanceContract: "bias_not_signal",
    dataLine: formatFbeDataLine(data, option),
    governanceLine: formatFbeGovernanceLine(data, option),
    calibrationLine: formatAccuracyLabel(option.sampleSize, option.winRate, option.avgBrier),
    // FBE remains LOG-ONLY. Do not echo payload-side approval flags into UI
    // state until an explicit execution redesign is approved and tested.
    executionApproved: false,
    isAvailable: true,
  };
}

export function formatFbeSetupList(items: string[]): string {
  return items.map(setupLabel).join(" · ");
}
